import {
  IsDuplicateResponse,
  OrderCustomerActionDTO,
  OrderCustomerActionDTOTypeIdEnum,
  OrderCustomerActionsApi,
  OrderCustomerDTO,
  OrderCustomersApi,
  OrdersApi,
  PaymentMethodChecksRequestDTOCustomerActionTypeEnum,
  PaymentMethodChecksResponseDTO,
} from '@reposit/api-client';
import { PaymentIntent, PaymentIntentResult, PaymentMethod, Stripe, StripeError } from '@stripe/stripe-js';
import { AxiosResponse } from 'axios';
import { push } from 'connected-react-router';
import { get } from 'lodash';
import { call, put, race, select, take, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage/index';
import { getErrorMessage } from '../../utils/common.utils';
import { sendEventToGoogleAnalytics } from '../../utils/integrators/analytics.integrator';
import { AddresHistoryActionTypes, setIsAddNewFormOpen } from '../address-history/address-history.actions';
import { setCardCheckErrorData, setCardCheckErrorModal } from '../card-check/card-check.actions';
import { CardCheckErrorData } from '../card-check/card-check.types';
import { syncEntitiesAndGetResults } from '../entities/entities.sagas';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import { setLoading } from '../loading/loading.actions';
import { fetchOrderCustomerRequested, OrderActionTypes } from '../order/order.actions';
import { getCurrentOrderCustomer } from '../order/order.selectors';
import SCHEMA from '../schema';
import { getCurrentUser } from '../selectors/user.selectors';
import { createOrderCustomerActionApi, createOrderCustomersApi, createOrdersApi, runSagaWithAuth } from '../utils/api.utils';
import { getStripe } from '../../utils/stripe';
import {
  CHECKOUT_PAYMENT_EXITED,
  confirmRepositFailed,
  confirmRepositSuccess,
  CONFIRM_REPOSIT_API_REQUESTED,
  CONFIRM_REPOSIT_STORE_KEY,
  getClaimPaymentIntentFailed,
  getClaimPaymentIntentSuccess,
  getPaymentIntentFailed,
  getPaymentIntentRequested,
  getPaymentIntentSuccess,
  GET_CLAIM_PAYMENT_INTENT_API_REQUESTED,
  GET_CLAIM_PAYMENT_INTENT_STORE_KEY,
  GET_PAYMENT_INTENT_API_REQUESTED,
  GET_PAYMENT_INTENT_STORE_KEY,
  OrderCustomerActionsActionTypes,
  payFailed,
  paySuccess,
  PAY_API_REQUESTED,
  PAY_STORE_KEY,
  pollPaymentSuccessFailed,
  pollPaymentSuccessRequested,
  pollPaymentSuccessSuccess,
  POLL_PAYMENT_SUCCESS_API_CANCELLED,
  POLL_PAYMENT_SUCCESS_API_FAILED,
  POLL_PAYMENT_SUCCESS_API_REQUESTED,
  POLL_PAYMENT_SUCCESS_API_SUCCESS,
  prepareCheckoutPaymentRequested,
  PREPARE_CHECKOUT_PAYMENT_API_REQUESTED,
  PREPARE_CHECKOUT_PAYMENT_STORE_KEY,
  signAddendumFailed,
  signAddendumSuccess,
  signRepositFailed,
  signRepositSuccess,
  SIGN_ADDENDUM_API_REQUESTED,
  SIGN_ADDENDUM_STORE_KEY,
  SIGN_REPOSIT_API_REQUESTED,
  SIGN_REPOSIT_STORE_KEY,
  updateAboutYouFailed,
  updateAboutYouSuccess,
  updateAddressHistoryFailed,
  updateAddressHistorySuccess,
  UPDATE_ABOUT_YOU_API_REQUESTED,
  UPDATE_ABOUT_YOU_STORE_KEY,
  UPDATE_ADDRESS_HISTORY_API_REQUESTED,
  UPDATE_ADDRESS_HISTORY_STORE_KEY,
} from './order-customer-actions.actions';
import { getPaymentIntentSecret } from './order-customer-actions.selectors';
import {
  addOrderCustomerActionBreadcrumb,
  captureOrderCustomerActionError,
  captureStripeError,
  SentryStripeErrorContext,
} from '../../utils/integrators/sentry.integrator';
import {
  OrderActionRepositPayload,
  PaymentType,
  PayPayload,
  PollPaymentSuccessPayload,
  SignAddendumPayload,
} from './order-customer-actions.types';

class CardCheckError extends Error {
  constructor(public failedChecks: CardCheckErrorData) {
    super('Card check failed');
    this.name = 'CardCheckError';
  }
}

// ****************
// WORKERS
// ****************
function delay(duration: number) {
  const promise = new Promise((resolve) => {
    setTimeout(() => resolve(true), duration);
  });
  return promise;
}

export function* updateAboutYou({ payload }: { type: string; payload: OrderActionRepositPayload }) {
  const { orderId, customerId } = payload;
  try {
    const { data } = payload;
    const orderCustomerActionsApi: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    const apiResponse = yield call(runSagaWithAuth(() => orderCustomerActionsApi.aboutYou(orderId, customerId, data)));

    yield syncEntitiesAndGetResults(apiResponse.data, SCHEMA.user);
    yield put<OrderCustomerActionsActionTypes>(updateAboutYouSuccess());
    yield put(push(`/checkout/${orderId}/${customerId}/`));
    yield put<OrderActionTypes>(
      fetchOrderCustomerRequested({
        customerId,
        orderId,
        source: {
          sourceType: 'saga-refresh',
          sourceName: 'update-about-you',
          parentAction: UPDATE_ABOUT_YOU_API_REQUESTED,
          reason: 'post-success-refresh',
        },
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    captureOrderCustomerActionError(e, { worker: 'updateAboutYou', orderId, customerId });
    yield put<OrderCustomerActionsActionTypes>(updateAboutYouFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ABOUT_YOU_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

export function* updateAddressHistory({ payload }: { type: string; payload: OrderActionRepositPayload }) {
  const { orderId, customerId } = payload;
  try {
    const orderCustomerActionsApi: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    const currentUser = yield select(getCurrentUser);
    const addresses = currentUser.attributes.addresses || [];

    yield call(
      runSagaWithAuth(() =>
        orderCustomerActionsApi.addressHistory(orderId, customerId, {
          addresses,
        })
      )
    );

    yield put<OrderCustomerActionsActionTypes>(updateAddressHistorySuccess());
    yield put<AddresHistoryActionTypes>(setIsAddNewFormOpen(false));
    yield put<OrderActionTypes>(
      fetchOrderCustomerRequested({
        customerId,
        orderId,
        source: {
          sourceType: 'saga-refresh',
          sourceName: 'update-address-history',
          parentAction: UPDATE_ADDRESS_HISTORY_API_REQUESTED,
          reason: 'post-success-refresh',
        },
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    captureOrderCustomerActionError(e, { worker: 'updateAddressHistory', orderId, customerId });
    yield put<OrderCustomerActionsActionTypes>(updateAddressHistoryFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: UPDATE_ADDRESS_HISTORY_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

export function* confirmReposit({ payload }: { type: string; payload: OrderActionRepositPayload }) {
  const { customerId, orderId } = payload;
  try {
    const orderCustomerActionsApi: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    yield call(runSagaWithAuth(() => orderCustomerActionsApi.confirm(orderId, customerId)));
    yield put<OrderCustomerActionsActionTypes>(confirmRepositSuccess());
    sendEventToGoogleAnalytics({
      action: 'Confirmed',
      category: 'Tenant',
      label: '',
    });
    yield put(push(`/checkout/${orderId}/${customerId}/`));
    yield put<OrderActionTypes>(
      fetchOrderCustomerRequested({
        customerId,
        orderId,
        source: {
          sourceType: 'saga-refresh',
          sourceName: 'confirm-reposit',
          parentAction: CONFIRM_REPOSIT_API_REQUESTED,
          reason: 'post-success-refresh',
        },
      })
    );
  } catch (e) {
    const error = get(e, 'response.data.message', e);
    captureOrderCustomerActionError(e, { worker: 'confirmReposit', orderId, customerId });
    yield put<OrderCustomerActionsActionTypes>(confirmRepositFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: CONFIRM_REPOSIT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

export function* signReposit({ payload }: { type: string; payload: OrderActionRepositPayload }) {
  const { customerId, orderId } = payload;
  try {
    const orderCustomerActionsApi: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    yield call(runSagaWithAuth(() => orderCustomerActionsApi.sign(orderId, customerId)));
    yield put<OrderCustomerActionsActionTypes>(signRepositSuccess());
    yield put(push(`/checkout/${orderId}/${customerId}/`));
    yield put<OrderActionTypes>(
      fetchOrderCustomerRequested({
        customerId,
        orderId,
        source: {
          sourceType: 'saga-refresh',
          sourceName: 'sign-reposit',
          parentAction: SIGN_REPOSIT_API_REQUESTED,
          reason: 'post-success-refresh',
        },
      })
    );
  } catch (e) {
    const error = get(e, 'response.data.message', e);
    captureOrderCustomerActionError(e, { worker: 'signReposit', orderId, customerId });
    yield put<OrderCustomerActionsActionTypes>(signRepositFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: SIGN_REPOSIT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

export function* signAddendum({ payload }: { type: string; payload: SignAddendumPayload }) {
  const { orderId, customerId } = payload;
  try {
    const api: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    yield call(runSagaWithAuth(() => api.signAddendumForTenant(orderId, customerId)));
    yield put<OrderCustomerActionsActionTypes>(signAddendumSuccess());
    yield put(push(`/checkout/${orderId}/${customerId}/`));
    yield put<OrderActionTypes>(
      fetchOrderCustomerRequested({
        customerId,
        orderId,
        source: {
          sourceType: 'saga-refresh',
          sourceName: 'sign-addendum',
          parentAction: SIGN_ADDENDUM_API_REQUESTED,
          reason: 'post-success-refresh',
        },
      })
    );
  } catch (e) {
    const error = getErrorMessage(e);
    captureOrderCustomerActionError(e, { worker: 'signAddendum', orderId, customerId });
    yield put<OrderCustomerActionsActionTypes>(signAddendumFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: SIGN_ADDENDUM_STORE_KEY, message: error, state: FlashState.ERROR })
    );
  }
}

export function* getPaymentIntent({ payload }: { type: string; payload: OrderActionRepositPayload }) {
  const { customerId, orderId } = payload;
  try {
    const orderCustomerActionsApi: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    const { data } = yield call(runSagaWithAuth(() => orderCustomerActionsApi.pay(orderId, customerId)));
    yield put<OrderCustomerActionsActionTypes>(getPaymentIntentSuccess(data.paymentIntentSecret));
  } catch (e) {
    const error = get(e, 'response.data.message', e);
    captureOrderCustomerActionError(e, { worker: 'getPaymentIntent', orderId, customerId });
    yield put<OrderCustomerActionsActionTypes>(getPaymentIntentFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: GET_PAYMENT_INTENT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

export function* getClaimPaymentIntent({ payload }: { type: string; payload: OrderActionRepositPayload }) {
  const { customerId, orderId, paymentType } = payload;
  try {
    const orderCustomerActionsApi: OrderCustomerActionsApi = yield createOrderCustomerActionApi();
    const { data } = yield call(runSagaWithAuth(() => orderCustomerActionsApi.pay(orderId, customerId, paymentType)));
    yield put<OrderCustomerActionsActionTypes>(getClaimPaymentIntentSuccess(data.paymentIntentSecret));
  } catch (e) {
    const error = get(e, 'response.data.message', e);
    captureOrderCustomerActionError(e, {
      worker: 'getClaimPaymentIntent',
      orderId,
      customerId,
      paymentType: paymentType as string | undefined,
    });
    yield put<OrderCustomerActionsActionTypes>(getClaimPaymentIntentFailed(error));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: GET_CLAIM_PAYMENT_INTENT_STORE_KEY,
        message: error,
        state: FlashState.ERROR,
      })
    );
  }
}

/**
 * Mobile browsers can discard the tab while the tenant approves a 3DS challenge in their banking
 * app, then reload the page when they return. The PaymentIntent outlives the reload (GET /pay
 * returns the same one), so its status decides whether to resume the challenge, finish a payment
 * that already went through, or let the tenant start from the card form.
 */
function* resumeCheckoutPayment({ payload }: ReturnType<typeof prepareCheckoutPaymentRequested>) {
  const { orderId, customerId } = payload;
  yield call(getPaymentIntent, getPaymentIntentRequested(customerId, orderId));
  const clientSecret: string = yield select(getPaymentIntentSecret);
  if (!clientSecret) {
    // getPaymentIntent has already reported the request error. After a reload the likely cause is that the
    // webhook completed the pay step between the checkout's order read and this request, which GET /pay
    // rejects, so the order is read again and a paid tenant finishes instead of seeing the card form.
    yield call(finishIfPaymentCompleted, { orderId, customerId });
    return;
  }

  const { order }: OrderCustomerDTO = yield select(getCurrentOrderCustomer);
  try {
    const stripe: Stripe | null = yield call(getStripe);
    if (!stripe) return;

    let step: SentryStripeErrorContext['step'] = 'retrieve_payment_intent';
    let result: PaymentIntentResult = yield call([stripe, stripe.retrievePaymentIntent], clientSecret);
    if (result.paymentIntent?.status === 'requires_action') {
      // Re-runs the pending 3DS challenge with the payment method already attached to the intent.
      step = 'resume_authentication';
      result = yield call([stripe, stripe.confirmCardPayment], clientSecret);
    }

    if (result.error) {
      captureStripeError(result.error, { worker: 'resumeCheckoutPayment', step, orderId, customerId });
      yield put<FlashMessagesActionTypes>(
        setFlashMessage({
          key: PAY_STORE_KEY,
          message: result.error.message || 'An error has occured',
          state: FlashState.ERROR,
        })
      );
      return;
    }

    const isPaid = result.paymentIntent?.status === 'succeeded' || result.paymentIntent?.status === 'processing';
    if (result.paymentIntent && isPaid) {
      yield call(completePayment, {
        paymentIntentId: result.paymentIntent.id,
        paymentIntentSecret: clientSecret,
        redirectUrl: `/checkout/${orderId}/${customerId}/finish`,
        externalRedirectUrl: order.redirectUrl,
      });
    }
  } catch (e) {
    // The card form still works with this secret, so the tenant can pay as they would without the check.
    captureOrderCustomerActionError(e, { worker: 'resumeCheckoutPayment', orderId, customerId });
  }
}

function* finishIfPaymentCompleted({ orderId, customerId }: OrderActionRepositPayload) {
  try {
    const { order }: OrderCustomerDTO = yield select(getCurrentOrderCustomer);
    const orderCustomersApi: OrderCustomersApi = yield createOrderCustomersApi();
    const { data }: AxiosResponse<OrderCustomerDTO> = yield call(
      runSagaWithAuth(() => orderCustomersApi.getOrderCustomerById(orderId, customerId))
    );
    const paymentAction = (data.actions as OrderCustomerActionDTO[]).find(
      (action) => action.typeId === OrderCustomerActionDTOTypeIdEnum.REPOSITPAY
    );
    if (!paymentAction || !paymentAction.completedAt) return;

    if (order.redirectUrl) {
      window.location.assign(order.redirectUrl);
      return;
    }
    yield put(push(`/checkout/${orderId}/${customerId}/finish`));
  } catch (e) {
    // The request error is already on screen; this is only a best-effort check behind it.
    captureOrderCustomerActionError(e, { worker: 'finishIfPaymentCompleted', orderId, customerId });
  }
}

export function* prepareCheckoutPayment(action: ReturnType<typeof prepareCheckoutPaymentRequested>) {
  yield put(setLoading({ action: PREPARE_CHECKOUT_PAYMENT_STORE_KEY, isLoading: true }));
  try {
    yield race({ resume: call(resumeCheckoutPayment, action), exited: take(CHECKOUT_PAYMENT_EXITED) });
  } finally {
    yield put(setLoading({ action: PREPARE_CHECKOUT_PAYMENT_STORE_KEY, isLoading: false }));
  }
}

function* completePayment({
  paymentIntentId,
  paymentIntentSecret,
  redirectUrl,
  externalRedirectUrl,
}: PollPaymentSuccessPayload & { externalRedirectUrl?: string }) {
  if (externalRedirectUrl) {
    window.location.assign(externalRedirectUrl);
    return;
  }
  yield put<OrderCustomerActionsActionTypes>(paySuccess());
  yield put<OrderCustomerActionsActionTypes>(pollPaymentSuccessRequested({ redirectUrl, paymentIntentId, paymentIntentSecret }));
}

const PAYMENT_FAILED_MESSAGE = 'Your payment was not successful. Please check your card details and try again.';

// A confirmed intent can still fail while we wait for the completion webhook (for example a `processing`
// payment the network then declines). Returns the reason when Stripe reports the intent has failed,
// and nothing when it is still on its way or Stripe cannot be reached, in which case we keep waiting.
function* getPaymentIntentFailure(paymentIntentSecret: string) {
  try {
    const stripe: Stripe | null = yield call(getStripe);
    if (!stripe) return undefined;
    const { paymentIntent }: PaymentIntentResult = yield call([stripe, stripe.retrievePaymentIntent], paymentIntentSecret);
    const hasFailed = paymentIntent?.status === 'requires_payment_method' || paymentIntent?.status === 'canceled';
    if (!hasFailed) return undefined;
    return paymentIntent.last_payment_error?.message || PAYMENT_FAILED_MESSAGE;
  } catch (e) {
    return undefined;
  }
}

export function* pay({ payload }: { type: string; payload: PayPayload }) {
  try {
    const { paymentIntentSecret, stripe, elements, type, singleTenant } = payload;
    const { order, customer } = yield select(getCurrentOrderCustomer);

    const getURL = (type: PaymentType) => {
      switch (type) {
        case PaymentType.REPOSIT:
          return `/checkout/${order.id}/${customer.id}/finish`;
        case PaymentType.CLAIM:
          return '/claims/payment-success';
        case PaymentType.CLAIM_REMAINING_BALANCE:
          return '/claims/payment-success-remaining-balance';
        case PaymentType.ARBITRATION_FEE:
          return '/claims/arbitration-payment-success';
        case PaymentType.ARBITRATION:
          return '/claims/payment-success';
        case PaymentType.ARBITRATION_REMAINING_BALANCE:
          return '/claims/payment-success';
        case PaymentType.TOP_UP:
          return '/annual-fee/payment-success';
        case PaymentType.CLAIM_PROPOSAL:
          return '/claims/proposal-payment-success';
      }
    };

    const customerActionType = (type: PaymentType) => {
      switch (type) {
        case PaymentType.REPOSIT:
          return PaymentMethodChecksRequestDTOCustomerActionTypeEnum.REPOSITPAY;
        case PaymentType.CLAIM:
          return PaymentMethodChecksRequestDTOCustomerActionTypeEnum.CLAIMPAY;
        case PaymentType.CLAIM_REMAINING_BALANCE:
          return singleTenant
            ? PaymentMethodChecksRequestDTOCustomerActionTypeEnum.CLAIMPAY
            : PaymentMethodChecksRequestDTOCustomerActionTypeEnum.CLAIMPAYREMAININGBALANCE;
        case PaymentType.ARBITRATION_FEE:
          return PaymentMethodChecksRequestDTOCustomerActionTypeEnum.ARBITRATIONADMINPAY;
        case PaymentType.ARBITRATION:
          return PaymentMethodChecksRequestDTOCustomerActionTypeEnum.ARBITRATIONPAY;
        case PaymentType.ARBITRATION_REMAINING_BALANCE:
          return singleTenant
            ? PaymentMethodChecksRequestDTOCustomerActionTypeEnum.ARBITRATIONPAY
            : PaymentMethodChecksRequestDTOCustomerActionTypeEnum.ARBITRATIONPAYREMAININGBALANCE;
        case PaymentType.TOP_UP:
          return PaymentMethodChecksRequestDTOCustomerActionTypeEnum.TOPUPPAY;
        case PaymentType.CLAIM_PROPOSAL:
          return PaymentMethodChecksRequestDTOCustomerActionTypeEnum.CLAIMPROPOSALPAYREMAININGBALANCE;
      }
    };

    const {
      paymentMethod,
      error,
    }: {
      paymentMethod?: PaymentMethod | undefined;
      error?: StripeError | undefined;
    } = yield stripe.createPaymentMethod({
      elements: elements as any,
    });

    if (!paymentMethod || error) {
      if (error) {
        captureStripeError(error, {
          worker: 'pay',
          step: 'create_payment_method',
          orderId: order?.id,
          customerId: customer?.id,
          paymentType: type,
        });
      }
      yield put<OrderCustomerActionsActionTypes>(payFailed(error && error.message ? error.message : 'An error has occured'));
      yield put<FlashMessagesActionTypes>(
        setFlashMessage({
          key: PAY_STORE_KEY,
          message: error && error.message ? error.message : 'An error has occured',
          state: FlashState.ERROR,
        })
      );
      return;
    }

    if (type === PaymentType.REPOSIT) {
      const ordersApi: OrdersApi = yield createOrdersApi();
      const { data }: { data: IsDuplicateResponse } = yield call(
        runSagaWithAuth(() => ordersApi.duplicatePaymentMethodCheck(order.id, { paymentMethodId: paymentMethod.id }))
      );

      if (data.isDuplicate) {
        // Duplicate card is an expected business-rule rejection surfaced to the user; breadcrumb only.
        addOrderCustomerActionBreadcrumb('Duplicate card rejected', {
          worker: 'pay',
          orderId: order?.id,
          customerId: customer?.id,
        });
        yield put<OrderCustomerActionsActionTypes>(payFailed('Duplicate card'));
        yield put<FlashMessagesActionTypes>(
          setFlashMessage({
            key: PAY_STORE_KEY,
            message:
              'The card you have used is shared with another tenant. To pay for Reposit you must use your own card that has not already been used.',
            state: FlashState.ERROR,
          })
        );
        return;
      }
    }

    const orderCustomersApi: OrderCustomersApi = yield createOrderCustomersApi();
    const { data }: { data: PaymentMethodChecksResponseDTO } = yield call(
      runSagaWithAuth(() =>
        orderCustomersApi.paymentCardChecks(order.id, customer.id, {
          paymentMethodId: paymentMethod.id,
          customerActionType: customerActionType(type),
        })
      )
    );

    const cardHasFailures = Object.values(data).includes('fail');

    if (cardHasFailures) {
      throw new CardCheckError(data);
    }
    const {
      paymentIntent,
      error: confirmError,
    }: {
      paymentIntent?: PaymentIntent | undefined;
      error?: StripeError | undefined;
    } = yield stripe.confirmCardPayment(paymentIntentSecret, { payment_method: paymentMethod.id });

    if (!paymentIntent || confirmError) {
      if (confirmError) {
        captureStripeError(confirmError, {
          worker: 'pay',
          step: 'confirm_card_payment',
          orderId: order?.id,
          customerId: customer?.id,
          paymentType: type,
        });
      }
      let paymentErrorMessage = confirmError && confirmError.message ? confirmError.message : 'An error has occured';
      yield put<OrderCustomerActionsActionTypes>(payFailed(paymentErrorMessage));
      yield put<FlashMessagesActionTypes>(
        setFlashMessage({
          key: PAY_STORE_KEY,
          message: paymentErrorMessage,
          state: FlashState.ERROR,
        })
      );
      return;
    }

    yield call(completePayment, {
      paymentIntentId: paymentIntent.id,
      paymentIntentSecret,
      redirectUrl: getURL(type) as string,
      externalRedirectUrl: order.redirectUrl,
    });
  } catch (e) {
    if (e instanceof CardCheckError) {
      // Card check failures are expected business-rule rejections shown to the user; record as breadcrumb only.
      const { order, customer } = yield select(getCurrentOrderCustomer);
      addOrderCustomerActionBreadcrumb('Card check failed', {
        worker: 'pay',
        orderId: order?.id,
        customerId: customer?.id,
      });
      yield put<OrderCustomerActionsActionTypes>(payFailed(e.message));
      yield put(setCardCheckErrorData(e.failedChecks));
      yield put(setCardCheckErrorModal(true));
    } else {
      const { order, customer } = yield select(getCurrentOrderCustomer);
      captureOrderCustomerActionError(e, {
        worker: 'pay',
        orderId: order?.id,
        customerId: customer?.id,
      });
      yield put<OrderCustomerActionsActionTypes>(payFailed(e));
      yield put<FlashMessagesActionTypes>(
        setFlashMessage({
          key: PAY_STORE_KEY,
          message: getErrorMessage(e),
          state: FlashState.ERROR,
        })
      );
    }
  }
}

export function* pollPaymentSuccess({ payload }: { type: string; payload: PollPaymentSuccessPayload }) {
  const { customer, order } = yield select(getCurrentOrderCustomer);
  try {
    const { paymentIntentId, paymentIntentSecret, redirectUrl } = payload;
    const orderCustomersApi: OrderCustomersApi = yield createOrderCustomersApi();
    let running = true;
    while (running) {
      const apiResponse: AxiosResponse<OrderCustomerDTO> = yield call(
        runSagaWithAuth(() => orderCustomersApi.getOrderCustomerById(order.id, customer.id))
      );
      const { data } = apiResponse;
      const paymentAction = (data.actions as OrderCustomerActionDTO[]).find((action: any) => {
        const details = action.details || {};
        const actionPaymentIntentId = details.stripePaymentIntentId;
        return actionPaymentIntentId === paymentIntentId;
      });

      if (paymentAction && paymentAction.completedAt) {
        running = false;
        yield put(push(redirectUrl));
        yield put<OrderCustomerActionsActionTypes>(pollPaymentSuccessSuccess());
        continue;
      }

      const failureMessage: string | undefined = yield call(getPaymentIntentFailure, paymentIntentSecret);
      if (failureMessage) {
        // Give the card form back with the reason, so the tenant can pay another way. The flash message
        // goes first because the watcher's race ends this saga as soon as the failure action is seen.
        running = false;
        yield put<FlashMessagesActionTypes>(
          setFlashMessage({ key: PAY_STORE_KEY, message: failureMessage, state: FlashState.ERROR })
        );
        yield put<OrderCustomerActionsActionTypes>(pollPaymentSuccessFailed(failureMessage));
        continue;
      }

      yield call(delay, 2000);
    }
  } catch (e) {
    captureOrderCustomerActionError(e, {
      worker: 'pollPaymentSuccess',
      orderId: order?.id,
      customerId: customer?.id,
    });
    yield put<OrderCustomerActionsActionTypes>(pollPaymentSuccessFailed(get(e, 'response.data.message', e)));
  }
}

// ****************
// WATCHERS
// ****************
export function* watchOrderCustomerActionSagas() {
  yield takeLatest(UPDATE_ABOUT_YOU_API_REQUESTED, updateAboutYou);
  yield takeLatest(UPDATE_ADDRESS_HISTORY_API_REQUESTED, updateAddressHistory);
  yield takeLatest(CONFIRM_REPOSIT_API_REQUESTED, confirmReposit);
  yield takeLatest(SIGN_REPOSIT_API_REQUESTED, signReposit);
  yield takeLatest(SIGN_ADDENDUM_API_REQUESTED, signAddendum);
  yield takeLatest(GET_PAYMENT_INTENT_API_REQUESTED, getPaymentIntent);
  yield takeLatest(PREPARE_CHECKOUT_PAYMENT_API_REQUESTED, prepareCheckoutPayment);
  yield takeLatest(GET_CLAIM_PAYMENT_INTENT_API_REQUESTED, getClaimPaymentIntent);
  yield takeLatest(PAY_API_REQUESTED, pay);

  while (true) {
    const action = yield take(POLL_PAYMENT_SUCCESS_API_REQUESTED);
    yield race({
      poll: call(pollPaymentSuccess, action),
      success: take(POLL_PAYMENT_SUCCESS_API_SUCCESS),
      cancelled: take(POLL_PAYMENT_SUCCESS_API_CANCELLED),
      exited: take(CHECKOUT_PAYMENT_EXITED),
      failed: take(POLL_PAYMENT_SUCCESS_API_FAILED),
    });
  }
}
